import { useCallback } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { filesApi as filesApiBase } from "src/api/endpoints";
import {
  FileUploadDataTypeEnum,
  FileUploadRequest,
  GetFileEnvFilesFileIdGetEnvEnum,
  UploadFileEnvFilesPostEnvEnum,
} from "src/clients/files";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { FileSource, PLACEHOLDER_FILE_ID } from "src/utils/files";

export const FILES_QUERY_KEY = ["uploaded-file"];

const fileKeys = {
  all: (env: GetFileEnvFilesFileIdGetEnvEnum, baseUrl?: string) => [
    "files",
    baseUrl,
    env,
  ],
  file: (
    env: GetFileEnvFilesFileIdGetEnvEnum,
    fileId: string | undefined,
    baseUrl?: string,
  ) => [...fileKeys.all(env, baseUrl), fileId],
  uploadedFile: (key: string | undefined) =>
    key ? [...FILES_QUERY_KEY, key] : FILES_QUERY_KEY,
};

export const useFile = (
  fileId: string | undefined,
  environment: GetFileEnvFilesFileIdGetEnvEnum | undefined,
  baseUrl: string | undefined,
) => {
  const { workspace } = useWorkspaceContext();
  const filesApi = filesApiBase(workspace.base_url!);
  return useQuery({
    queryKey: fileKeys.file(environment!, fileId, baseUrl),
    queryFn: () =>
      filesApi.getFileEnvFilesFileIdGet({ fileId: fileId!, env: environment! }),
    enabled: Boolean(
      baseUrl && environment && fileId && fileId !== PLACEHOLDER_FILE_ID,
    ),
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

export type UseUploadFileReturn = {
  onFileSelect: (file: File, key: string) => void;
  onFileUpload: (
    file: File,
    key: string,
    env: UploadFileEnvFilesPostEnvEnum,
    fileSource: FileSource,
  ) => Promise<string | undefined>;
  onFileRemove: (key: string) => void;
  removeAllFiles: () => void;
  isLoading: boolean;
  getFile: (key: string) => File | undefined;
  getAllFiles: () => Map<string, File>;
  getFilesForField: (fieldKey: string) => Map<string, File>;
  updateFileName: (name: string, key: string) => void;
};

export const useUploadFile = (): UseUploadFileReturn => {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspaceContext();

  const { mutate: selectFile, isPending: isSelectFilePending } = useMutation({
    mutationFn: async ({
      file,
      name,
      key,
    }: {
      file: File;
      name?: string;
      key?: string;
    }) => {
      const renamedFile = name
        ? new File([file], name, { type: file.type })
        : file;
      queryClient.setQueryData(fileKeys.uploadedFile(key), renamedFile);
      return Promise.resolve(renamedFile);
    },
  });

  const { mutate: removeFile } = useMutation({
    mutationFn: async (key: string) => {
      queryClient.setQueryData(fileKeys.uploadedFile(key), null);
      return Promise.resolve();
    },
  });

  const removeAllFiles = useCallback(() => {
    queryClient.removeQueries({
      queryKey: FILES_QUERY_KEY,
    });
  }, [queryClient]);

  const getFile = useCallback(
    (key: string) => {
      const res = queryClient.getQueryData<File>(fileKeys.uploadedFile(key));
      return res;
    },
    [queryClient],
  );

  const getAllFiles = useCallback(() => {
    const files = new Map<string, File>();
    const queries = queryClient.getQueriesData<File>({
      queryKey: FILES_QUERY_KEY,
    });
    queries.forEach(([queryKey, file]) => {
      if (file && queryKey.length > 1) {
        files.set(queryKey[1] as string, file);
      }
    });
    return files;
  }, [queryClient]);

  // For multi-file fields ("files" type), each file is stored under a unique
  // key. This method retrieves all files belonging to a given field by prefix-matching on "fieldName::".
  const getFilesForField = useCallback(
    (fieldKey: string) => {
      const files = new Map<string, File>();
      const queries = queryClient.getQueriesData<File>({
        queryKey: FILES_QUERY_KEY,
      });
      const prefix = `${fieldKey}::`;
      queries.forEach(([queryKey, file]) => {
        if (file && queryKey.length > 1) {
          const key = queryKey[1] as string;
          if (key.startsWith(prefix)) {
            files.set(key, file);
          }
        }
      });
      return files;
    },
    [queryClient],
  );

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: async ({
      file,
      env,
      fileSource,
    }: {
      file: File;
      env: UploadFileEnvFilesPostEnvEnum;
      fileSource: FileSource;
    }) => {
      const filesApi = filesApiBase(workspace.base_url!);
      const fileAttributes: FileUploadRequest = {
        data: {
          type: "file_uploads" as FileUploadDataTypeEnum,
          attributes: {
            name: file.name,
          },
        },
      };
      const fileId = await filesApi.uploadFileEnvFilesPost({
        fileUploadRequest: fileAttributes,
        env: env,
        xFileSource: fileSource,
      });
      return Promise.resolve(fileId);
    },
  });

  const onFileSelect = useCallback(
    (file: File, key: string) => {
      selectFile({ file: file, key: key });
    },
    [selectFile],
  );

  const updateFileName = useCallback(
    (name: string, key: string) => {
      const currentFile = getFile(key);
      if (currentFile) {
        selectFile({ file: currentFile, name: name, key: key });
      }
    },
    [getFile, selectFile],
  );

  const onFileUpload = useCallback(
    async (
      file: File,
      key: string,
      env: UploadFileEnvFilesPostEnvEnum,
      fileSource: FileSource,
    ): Promise<string | undefined> => {
      const response = await uploadFile({
        file,
        env,
        fileSource,
      });
      const { upload_url, form_fields } = response.data?.data?.attributes || {};

      if (upload_url && form_fields) {
        const formData = new FormData();
        Object.entries(form_fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append("file", file);

        await fetch(upload_url, {
          method: "POST",
          body: formData,
        });
      }

      return response.data?.data?.id;
    },
    [uploadFile],
  );

  const onFileRemove = useCallback(
    (key: string) => {
      removeFile(key);
    },
    [removeFile],
  );

  return {
    onFileSelect,
    onFileUpload,
    onFileRemove,
    removeAllFiles,
    isLoading: isSelectFilePending,
    getFile,
    getAllFiles,
    getFilesForField,
    updateFileName,
  };
};
